// src/pages/UserDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/UserDashboard.css';
import LaunchWaitOverlay, { unlockLaunchAudio } from '../components/LaunchWaitOverlay';

const APPLICATIONS = {
  PART1: {
    id: "PART1",
    name: "MISSING: The Search for Terry Hughes",
    shortName: "MISSING Part I",
    description: "As an investigator, you'll search for Terry Hughes who has disappeared. Navigate complex situations, collect evidence, while learning to recognize and mitigate confirmation bias, the fundamental attribution error, and bias blind spot that can affect your judgment."
  },
  PART2: {
    id: "PART2",
    name: "MISSING: The Final Secret",
    shortName: "MISSING Part II",
    description: "Continue your journey in cognitive bias training with new challenges and scenarios, learning to address anchoring bias, the representativeness heuristic, and projection bias."
  }
};

// Debug logging function
const debugLog = (message, data) => {
  console.log(`[DEBUG] ${message}`, data);
  // You can also log to a service like LogRocket or Sentry here if available
};

const UserDashboard = () => {
  const [userProgress, setUserProgress] = useState({});
  const [availableApps, setAvailableApps] = useState(["PART1", "PART2"]); // Default to showing both
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [waitStatus, setWaitStatus] = useState(null); // set when the server says the session is still starting
  const [selectedApp, setSelectedApp] = useState("PART1"); // Default to Part I
  const navigate = useNavigate();

  // Format time from seconds to hours and minutes
  const formatPlayTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Fetch available applications and progress for the user
  useEffect(() => {
    const fetchUserData = async () => {
      debugLog("Starting fetchUserData", null);
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          debugLog("No token found, redirecting to login", null);
          navigate('/');
          return;
        }

        // Get user email from token
        let userEmail = '';
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            userEmail = payload.email || payload.username || '';
            debugLog("User email from token:", userEmail);
          }
        } catch (tokenErr) {
          debugLog('Error extracting email from token:', tokenErr);
        }

        // Initialize with both apps - we'll filter by what's accessible
        // This is a workaround for the IAM permission issue
        const defaultApps = ["PART1", "PART2"];
        const accessibleApps = [];
        const progressData = {};

        // Try to fetch progress for each app to determine access
        for (const app of defaultApps) {
          try {
            debugLog(`Checking access for ${app}...`, null);
            const progressUrl = `https://admin.cretecinc.com/user/progress?application=${app}`;
            debugLog(`Making request to: ${progressUrl}`, null);
            
            const progressResponse = await fetch(progressUrl, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
              }
            });

            const responseStatus = progressResponse.status;
            debugLog(`Response status for ${app}:`, responseStatus);
            
            if (progressResponse.ok) {
              debugLog(`User has access to ${app}`, null);
              accessibleApps.push(app);
              
              // Store progress data - Added more robust handling
              const responseData = await progressResponse.json();
              debugLog(`Raw response data for ${app}:`, responseData);
              
              if (responseData && responseData.progress) {
                debugLog(`Progress data structure for ${app}:`, responseData.progress);
                
                // Log more details about the progress object
                if (responseData.progress) {
                  debugLog(`Progress object type:`, typeof responseData.progress);
                  debugLog(`Progress object keys:`, Object.keys(responseData.progress));
                  debugLog(`gameComplete value:`, responseData.progress.gameComplete);
                  debugLog(`gameCompleted value (if exists):`, 
                    'gameCompleted' in responseData.progress ? responseData.progress.gameCompleted : 'field does not exist');
                }
                
                progressData[app] = responseData.progress;
                debugLog(`Progress data saved for ${app}:`, responseData.progress);
              } else {
                debugLog(`No progress data found for ${app} or data is in unexpected format`, responseData);
              }
            } else {
              debugLog(`User does not have access to ${app} or there was an error`, {
                status: responseStatus,
                statusText: progressResponse.statusText
              });
              
              // Try to parse error response if possible
              try {
                const errorData = await progressResponse.json();
                debugLog(`Error response for ${app}:`, errorData);
              } catch (parseErr) {
                debugLog(`Could not parse error response for ${app}`, parseErr);
              }
            }
          } catch (progressErr) {
            debugLog(`Error checking access for ${app}:`, progressErr);
            // On error, assume access might be valid and include it (fail open)
            accessibleApps.push(app);
          }
        }
        
        // Update state with all progress data at once
        setUserProgress(progressData);
        debugLog("Final progress data state:", progressData);

        // If we couldn't determine access, default to showing both
        if (accessibleApps.length === 0) {
          debugLog("No accessible apps detected, defaulting to both", null);
          setAvailableApps(defaultApps);
        } else {
          debugLog("Setting available apps to:", accessibleApps);
          setAvailableApps(accessibleApps);
        }

        // Set selectedApp based on available apps
        if (accessibleApps.length === 1) {
          debugLog("Only one app available, selecting it:", accessibleApps[0]);
          setSelectedApp(accessibleApps[0]);
        } else if (accessibleApps.length > 1) {
          // If user has access to both but has already completed Part I, select Part II
          if (progressData["PART1"] && progressData["PART1"].gameComplete) {
            debugLog("User completed PART1, selecting PART2", null);
            setSelectedApp("PART2");
          } else {
            debugLog("Defaulting to PART1", null);
            setSelectedApp("PART1"); // Default to Part I
          }
        }
      } catch (err) {
        debugLog('Error fetching user data:', err);
        setError('Could not load your available applications and progress. Please try again later.');
      } finally {
        setLoading(false);
        debugLog("Finished fetchUserData", null);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Launch the selected game
  const launchGame = async () => {
    debugLog("Launch button clicked for:", selectedApp);
    unlockLaunchAudio(selectedApp); // allow the "ready" announcement to play later (browser autoplay rules)
    setLaunching(true);
    setError(null); // Clear any previous errors
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        debugLog("No token found for launch, redirecting to login", null);
        navigate('/');
        return;
      }

      debugLog("Making launch request for:", selectedApp);
      const response = await fetch('https://admin.cretecinc.com/user/launch-game', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          application: selectedApp
        })
      });

      debugLog("Launch response status:", response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        debugLog("Launch error:", errorData);
        throw new Error(errorData.reason || 'Failed to launch game');
      }

      const data = await response.json();

      // Zero-warm launch: the session is still being prepared -> show the wait screen.
      if (response.status === 202) {
        debugLog("Session starting, showing wait screen:", data);
        setWaitStatus(data);
        return;
      }

      debugLog("Launch successful, redirecting to:", data.streamingUrl);
      window.location.href = data.streamingUrl; // Redirect to AppStream
    } catch (err) {
      debugLog('Error launching game:', err);
      setError(`Could not launch ${APPLICATIONS[selectedApp].shortName}: ${err.message}`);
    } finally {
      setLaunching(false);
    }
  };

  // Function to show certificate link for completed games
  const showCertificateLink = () => {
    const currentProgress = userProgress[selectedApp];
    debugLog("showCertificateLink check for:", { selectedApp, currentProgress });
    
    if (currentProgress && currentProgress.gameComplete) {
      debugLog("Game is complete, showing certificate link", null);
      // Get the user's email from wherever it's available
      let userEmail = '';
      
      // Try to get email from JWT token
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            if (payload.email) {
              userEmail = payload.email;
            } else if (payload.username) {
              userEmail = payload.username;
            }
          }
        }
      } catch (error) {
        debugLog('Error extracting email from token for certificate:', error);
      }
      
      debugLog("Certificate user email:", userEmail);
      
      return (
        <div className="certificate-section">
          <h3>Congratulations on completing {APPLICATIONS[selectedApp].shortName}!</h3>
          <p>You have successfully completed the training and learned valuable skills in recognizing cognitive biases.</p>
          {userEmail ? (
            <a 
              href={`https://admin.cretecinc.com/certificate-name?email=${encodeURIComponent(userEmail)}&application=${selectedApp}`}
              className="certificate-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Your Certificate
            </a>
          ) : (
            <p className="error-message">
              Unable to retrieve your email. Please check your email for a certificate link or contact support.
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Store user email in localStorage when component mounts
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Try to extract email from token if it's a JWT
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.email) {
            localStorage.setItem('userEmail', payload.email);
            debugLog("Stored user email in localStorage:", payload.email);
          }
        }
      } catch (err) {
        debugLog('Error storing email in localStorage:', err);
      }
    }
  }, []);

  if (loading) {
    return <div className="dashboard-container"><p>Loading your progress...</p></div>;
  }

  // Get current application details
  const currentApp = APPLICATIONS[selectedApp];
  const currentProgress = userProgress[selectedApp];
  
  // Log detailed information about current app and progress at render time
  debugLog("Rendering with selectedApp:", selectedApp);
  debugLog("currentApp:", currentApp);
  debugLog("currentProgress:", currentProgress);
  
  if (currentProgress) {
    debugLog("currentProgress type:", typeof currentProgress);
    debugLog("currentProgress keys:", Object.keys(currentProgress));
    debugLog("gameComplete value:", currentProgress.gameComplete);
  }
  
  // Log the condition that determines progress display
  const hasProgress = currentProgress && (typeof currentProgress === 'object');
  debugLog("Progress condition evaluation:", {
    currentProgressExists: !!currentProgress,
    currentProgressType: typeof currentProgress,
    hasProgress: hasProgress
  });

  return (
    <div className="dashboard-container">
      {waitStatus && (
        <LaunchWaitOverlay
          application={waitStatus.application || selectedApp}
          appName={APPLICATIONS[waitStatus.application || selectedApp].shortName}
          launchEndpoint="https://admin.cretecinc.com/user/launch-game"
          initialStatus={waitStatus}
          onClose={() => setWaitStatus(null)}
        />
      )}
      {availableApps.length > 1 && (
        <div style={{ 
          border: "1px solid #ccc", 
          padding: "1rem", 
          marginBottom: "1rem",
          borderRadius: "4px" 
        }}>
          <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Select Application</h2>
          <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
            {availableApps.map(appId => (
              <button
                key={appId}
                onClick={() => {
                  debugLog("App selection button clicked:", appId);
                  setSelectedApp(appId);
                }}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: selectedApp === appId ? "#4CAF50" : "#e0e0e0",
                  color: selectedApp === appId ? "white" : "black",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: selectedApp === appId ? "bold" : "normal"
                }}
              >
                {APPLICATIONS[appId].shortName}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <h1>{currentApp.name}</h1>
      
      <div className="progress-container">
        <h2>Your Progress</h2>
        
        {currentProgress && (typeof currentProgress === 'object') ? (
          <div className="progress-stats">
            <div className="stat">
              <span className="label">Play Time:</span>
              <span className="value">{formatPlayTime(currentProgress.playTime)}</span>
            </div>
            <div className="stat">
              <span className="label">Current Checkpoint:</span>
              <span className="value">{currentProgress.checkPoint}</span>
            </div>
            <div className="stat">
              <span className="label">AAR Checkpoint:</span>
              <span className="value">{currentProgress.aarCheckpoint}</span>
            </div>
            <div className="stat">
              <span className="label">Last Played:</span>
              <span className="value">{formatDate(currentProgress.lastUpdated)}</span>
            </div>
            <div className="stat">
              <span className="label">Status:</span>
              <span className="value status-indicator">
                {currentProgress.gameComplete ? 
                  <span className="completed">Completed</span> : 
                  <span className="in-progress">In Progress</span>
                }
              </span>
            </div>
          </div>
        ) : (
          <div>
            <p>You haven't started {currentApp.shortName} yet. Click the button below to begin your investigation.</p>
          </div>
        )}
        
        {error && <div className="error-message">{error}</div>}
        
        {currentProgress && currentProgress.gameComplete ? (
          <div className="completion-message">
            <p>You have completed {currentApp.shortName}! Thank you for participating in the training.</p>
          </div>
        ) : (
          <button 
            className="launch-button" 
            onClick={launchGame} 
            disabled={launching}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: launching ? "default" : "pointer",
              fontSize: "1rem",
              fontWeight: "bold",
              marginTop: "1rem",
              width: "100%",
              maxWidth: "300px"
            }}
          >
            {launching ? 'Launching...' : currentProgress ? `Resume ${currentApp.shortName}` : `Start ${currentApp.shortName}`}
          </button>
        )}
        
        {/* Show certificate link if game is completed */}
        {showCertificateLink()}
      </div>
      
      <div className="game-info">
        <h3>About {currentApp.name}</h3>
        <p>{currentApp.description}</p>
      </div>
     <div style={{ marginTop: "2rem", textAlign: "center", fontSize: "0.9rem" }}>
      Need help?{" "}
      <a 
        href="mailto:support@creativetechnologiesincla.zohodesk.com" 
        style={{ color: "#2196F3", textDecoration: "none" }}
      >
        Contact Support
      </a>
    </div>
    </div>
  );
};

export default UserDashboard;